import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFilters } from "../../../../../../../utils/hooks/useFilters";
import { MoveUpRight, MoveDownLeft, Equal } from "lucide-react";
import Spinner from "../../../../../../../components/shared/Spinner";
import { cn } from "../../../../../../../lib/utils";

interface ItemProps {
    title: string;
    icon: any;
    value: string;
    percentage?: string;
    trend?: "up" | "down" | "equal";
    loading: boolean
}

function Item({ title, icon: Icon, value, percentage, trend, loading }: ItemProps) {
    const { t } = useTranslation();
    const [{ mode }] = useFilters();
    const isUp = trend === "up";
    const isEqual = trend === "equal";
    const TrendIcon = isUp ? MoveUpRight : MoveDownLeft;

    const percentageLabel = useMemo(() => {
        let label = "from-yesterday";
        switch (mode) {
            default:
            case "day": {
                label = "from-yesterday";
                break;
            }
            case "week": {
                label = "from-last-week";
                break;
            }
            case "month": {
                label = "from-last-month";
                break;
            }
            case "custom":
            case "pay-group": {
                label = "from-last-period";
            }
        }
        return t(label);
    }, [mode, t]);

    return (
        <div className="tw-border tw-border-gray-200 tw-p-4 tw-rounded-xl tw-bg-white">
            {loading ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-7">
                    <Spinner className="tw-text-primary" />
                </div>
            ) : (
                <>
                    <div className="tw-flex tw-items-start tw-justify-between tw-mb-3">
                        <p className="tw-text-xs tw-font-medium tw-text-gray-500 tw-leading-snug tw-pr-2">{title}</p>
                        <div className="tw-shrink-0 tw-p-1.5 tw-rounded-lg tw-bg-primary/10">
                            <Icon size={15} className="tw-text-primary" />
                        </div>
                    </div>

                    <p className="tw-font-bold tw-text-2xl tw-text-gray-900 tw-mb-2">
                        {value}
                    </p>

                    {percentage && (
                        <div className={cn(
                            "tw-inline-flex tw-items-center tw-gap-1 tw-px-1.5 tw-py-0.5 tw-rounded-md tw-text-xs tw-font-medium",
                            isEqual
                                ? "tw-bg-primary/10 tw-text-primary"
                                : isUp
                                    ? "tw-bg-green-50 tw-text-green-600"
                                    : "tw-bg-red-50 tw-text-red-500"
                        )}>
                            {isEqual ? <Equal size={13} /> : <TrendIcon size={13} />}
                            {!isEqual && <span>{percentage}</span>}
                            <span className="tw-font-normal tw-opacity-80">{percentageLabel}</span>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default Item;
